import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { ApiCall } from "../helper/axios";
// ✅ Type guard to check if response is AxiosResponse
const isAxiosResponse = (response) => {
    return response && typeof response === 'object' && 'data' in response && !('isAxiosError' in response);
};
// ✅ Async Thunk: Fetch Campaign Preview (Fast)
export const fetchCampaignPreview = createAsyncThunk("campaignPreview/fetchPreview", async (params, { getState, rejectWithValue }) => {
    try {
        const { auth } = getState();
        const token = auth?.token;
        const response = await ApiCall("POST", "/api/klaviyo-campaign-preview", params, {
            Authorization: token || ""
        });
        if (isAxiosResponse(response) && response.data?.success) {
            return response.data.data;
        }
        else {
            return rejectWithValue("Failed to fetch campaign preview");
        }
    }
    catch (error) {
        return rejectWithValue(error?.response?.data?.message ||
            error?.message ||
            "An error occurred while fetching preview");
    }
});
// ✅ Async Thunk: Fetch Campaign Audience (Slow)
export const fetchCampaignAudience = createAsyncThunk("campaignPreview/fetchAudience", async (params, { getState, rejectWithValue }) => {
    try {
        const { auth } = getState();
        const token = auth?.token;
        const response = await ApiCall("POST", "/api/klaviyo-campaign-audience", params, {
            Authorization: token || ""
        });
        if (isAxiosResponse(response) && response.data?.success) {
            return response.data.data;
        }
        else {
            return rejectWithValue("Failed to fetch campaign audience");
        }
    }
    catch (error) {
        return rejectWithValue(error?.response?.data?.message ||
            error?.message ||
            "An error occurred while fetching audience");
    }
});
// ✅ Initial State
const initialState = {
    // Preview
    previewData: null,
    previewLoading: false,
    previewError: null,
    lastFetchedMessageId: null,
    // Audience
    audienceData: null,
    audienceLoading: false,
    audienceError: null,
};
// ✅ Slice
const campaignPreviewSlice = createSlice({
    name: "campaignPreview",
    initialState,
    reducers: {
        // Clear all data
        clearCampaignPreview: (state) => {
            state.previewData = null;
            state.previewError = null;
            state.previewLoading = false;
            state.lastFetchedMessageId = null;
            state.audienceData = null;
            state.audienceError = null;
            state.audienceLoading = false;
        },
        // Clear only preview
        clearPreviewData: (state) => {
            state.previewData = null;
            state.previewError = null;
            state.previewLoading = false;
            state.lastFetchedMessageId = null;
        },
        // Clear only audience
        clearAudienceData: (state) => {
            state.audienceData = null;
            state.audienceError = null;
            state.audienceLoading = false;
        },
        // Clear errors
        clearPreviewError: (state) => {
            state.previewError = null;
        },
        clearAudienceError: (state) => {
            state.audienceError = null;
        },
    },
    extraReducers: (builder) => {
        builder
            // ✅ Campaign Preview
            .addCase(fetchCampaignPreview.pending, (state) => {
            state.previewLoading = true;
            state.previewError = null;
        })
            .addCase(fetchCampaignPreview.fulfilled, (state, action) => {
            state.previewLoading = false;
            state.previewData = action.payload;
            state.previewError = null;
            state.lastFetchedMessageId = action.meta.arg.messageId;
        })
            .addCase(fetchCampaignPreview.rejected, (state, action) => {
            state.previewLoading = false;
            state.previewError = action.payload || "Failed to fetch preview";
            state.previewData = null;
        })
            // ✅ Campaign Audience
            .addCase(fetchCampaignAudience.pending, (state) => {
            state.audienceLoading = true;
            state.audienceError = null;
        })
            .addCase(fetchCampaignAudience.fulfilled, (state, action) => {
            state.audienceLoading = false;
            state.audienceData = action.payload;
            state.audienceError = null;
        })
            .addCase(fetchCampaignAudience.rejected, (state, action) => {
            state.audienceLoading = false;
            state.audienceError = action.payload || "Failed to fetch audience";
            state.audienceData = null;
        });
    },
});
// ✅ Actions
export const { clearCampaignPreview, clearPreviewData, clearAudienceData, clearPreviewError, clearAudienceError, } = campaignPreviewSlice.actions;
// ✅ Preview Selectors
export const selectCampaignPreviewData = (state) => state.campaignPreview.previewData;
export const selectCampaignPreviewLoading = (state) => state.campaignPreview.previewLoading;
export const selectCampaignPreviewError = (state) => state.campaignPreview.previewError;
export const selectLastFetchedMessageId = (state) => state.campaignPreview.lastFetchedMessageId;
// ✅ Audience Selectors
export const selectCampaignAudienceData = (state) => state.campaignPreview.audienceData;
export const selectCampaignAudienceLoading = (state) => state.campaignPreview.audienceLoading;
export const selectCampaignAudienceError = (state) => state.campaignPreview.audienceError;
export const selectIncludedListsAndSegments = (state) => state.campaignPreview.audienceData?.includedListsAndSegments || "";
export const selectExcludedListsAndSegments = (state) => state.campaignPreview.audienceData?.excludedListsAndSegments || "";
// ✅ Derived Selectors
export const selectAudienceSegments = (state) => state.campaignPreview.audienceData?.segments || [];
export const selectAudienceLists = (state) => state.campaignPreview.audienceData?.lists || [];
export const selectTotalAudienceCount = (state) => {
    const data = state.campaignPreview.audienceData;
    if (!data)
        return 0;
    const segmentCount = data.segments?.reduce((acc, seg) => {
        return acc + (seg.klaviyo?.attributes?.profile_count || 0);
    }, 0) || 0;
    const listCount = data.lists?.reduce((acc, list) => {
        return acc + (list.klaviyo?.attributes?.profile_count || 0);
    }, 0) || 0;
    return segmentCount + listCount;
};
// ✅ Combined loading state
export const selectIsAnyLoading = (state) => state.campaignPreview.previewLoading || state.campaignPreview.audienceLoading;
export default campaignPreviewSlice.reducer;
